import { Controller } from '@hotwired/stimulus';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const roundToStep = (value, step) =>
  Number(value.toFixed((String(step).split('.')[1] || '').length));

export default class extends Controller {
  static targets = ['input', 'stepDown', 'stepUp'];

  static values = {
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    step: { type: Number, default: 1 }
  };

  connect() {
    this.refresh();
  }

  // ── Public actions (wired from data-action) ────────────────────────────────
  handleInput() {
    this.commit(Number(this.input.value));
  }

  stepDown() {
    this.commit(this.currentValue - this.stepValue);
  }

  stepUp() {
    this.commit(this.currentValue + this.stepValue);
  }

  // ── Internals ──────────────────────────────────────────────────────────────
  commit(next) {
    const clamped = clamp(roundToStep(next, this.stepValue), this.minValue, this.maxValue);
    this.input.value = String(clamped);
    this.refresh();
    this.dispatch('change', { detail: { value: clamped } });
  }

  // Keeps the filled portion of the track and the stepper bounds in sync with
  // the input — the CSS reads the fill from `--ui-slider-fill`.
  refresh() {
    const value = this.currentValue;
    const range = this.maxValue - this.minValue;
    const fill = range === 0 ? 0 : ((value - this.minValue) / range) * 100;

    this.input.style.setProperty('--ui-slider-fill', `${fill}%`);

    if (this.hasStepDownTarget) {
      this.stepDownTarget.disabled = this.input.disabled || value <= this.minValue;
    }
    if (this.hasStepUpTarget) {
      this.stepUpTarget.disabled = this.input.disabled || value >= this.maxValue;
    }
  }

  // Without steppers the controller is attached to the input itself, so there
  // is no descendant target to look up.
  get input() {
    return this.hasInputTarget ? this.inputTarget : this.element;
  }

  get currentValue() {
    return Number(this.input.value);
  }
}
